import { useEffect, useState, type FormEvent } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router";
import { useAuth } from "../auth/AuthProvider";
import { ApiError } from "../lib/api";
import { AuthShell, CodeInput, PasswordInput } from "../components/AuthShell";
import { Alert, Button, Field, Input, LoadingBlock } from "../components/ui";
import { verifyFailure } from "../lib/twoFactor";

// Reissued challenges currently omit their TTL. Initial challenges always use
// the server-provided value; this only avoids keeping a spent token forever.
const FALLBACK_CHALLENGE_TTL_MS = 5 * 60 * 1000;

function destinationFrom(state: unknown): string {
  const from =
    state && typeof state === "object" && "from" in state && typeof state.from === "string"
      ? state.from
      : null;
  return from?.startsWith("/") && !from.startsWith("//") ? from : "/";
}

export function LoginPage() {
  const { status, login, verifyTwoFactor } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const destination = destinationFrom(location.state);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const [challengeExpiry, setChallengeExpiry] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [lockedOut, setLockedOut] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);

  // Challenge expiry countdown
  useEffect(() => {
    if (!challengeExpiry || lockedOut) {
      if (!challengeExpiry) setTimeRemaining(null);
      return;
    }

    const update = () => {
      const remaining = Math.max(0, challengeExpiry - Date.now());
      setTimeRemaining(remaining);
      if (remaining === 0) {
        setChallengeToken(null);
        setChallengeExpiry(null);
        setUseRecoveryCode(false);
        setError("Code expired. Please sign in again.");
      }
    };

    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [challengeExpiry, lockedOut]);

  if (status === "loading") return <LoadingBlock label="Restoring session…" />;
  if (status === "authenticated") return <Navigate to={destination} replace />;
  if (status === "forbidden") return <Navigate to="/" replace />;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const outcome = await login(email, password);
      if (outcome.kind === "challenge") {
        setChallengeToken(outcome.challengeToken);
        setChallengeExpiry(Date.now() + outcome.expiresIn * 1000);
        setLockedOut(false);
        setCode("");
        setUseRecoveryCode(false);
      } else {
        void navigate(destination, { replace: true });
      }
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Could not sign in. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  async function onVerify(event: FormEvent) {
    event.preventDefault();
    if (!challengeToken || lockedOut) return;
    setError(null);
    setSubmitting(true);
    try {
      await verifyTwoFactor(challengeToken, code);
      void navigate(destination, { replace: true });
    } catch (cause) {
      // A transport failure does not spend a server challenge. Keep the password
      // proof and let the operator retry rather than making them start over.
      if (!(cause instanceof ApiError) || cause.status >= 500) {
        setError("We could not verify that code. Check your connection and try again.");
        return;
      }

      const failure = verifyFailure(cause);
      if (failure.restart) {
        const isLockout = cause.message.includes("Too many attempts");
        if (isLockout) {
          setLockedOut(true);
          setCode("");
          setError(cause.message);
        } else {
          setChallengeToken(null);
          setChallengeExpiry(null);
          setPassword("");
          setCode("");
          setUseRecoveryCode(false);
          setError(cause.message);
        }
      } else {
        setChallengeToken(failure.nextChallengeToken ?? challengeToken);
        setChallengeExpiry(Date.now() + FALLBACK_CHALLENGE_TTL_MS);
        setCode("");
        setError(`Incorrect code. ${6 - failure.attempts} attempts remaining.`);
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (challengeToken) {
    const challengeSeconds = timeRemaining !== null ? Math.ceil(timeRemaining / 1000) : 0;
    const challengeMinutes = Math.floor(challengeSeconds / 60);
    const challengeRemainingSeconds = challengeSeconds % 60;

    return (
      <AuthShell
        title="Verify your sign-in"
        subtitle="Use your authenticator app or one of the recovery codes you saved."
        footer={
          <button
            type="button"
            onClick={() => {
              setChallengeToken(null);
              setChallengeExpiry(null);
              setLockedOut(false);
              setPassword("");
              setCode("");
              setUseRecoveryCode(false);
              setError(null);
            }}
            className="text-sm text-fg-subtle transition-colors duration-200 ease-smooth hover:text-fg"
          >
            Back to sign in
          </button>
        }
      >
        <div className="space-y-5">
          <div className="border-b border-line pb-4 text-center">
            <p className="micro text-accent">Staff account</p>
            <p className="mt-2 break-all text-sm font-medium text-fg">{email}</p>
          </div>

          {lockedOut ? (
            <Alert>
              {error ?? "Too many attempts. Wait a few minutes before trying again."}
            </Alert>
          ) : (
            error && <Alert>{error}</Alert>
          )}

          {!lockedOut && (
            <form onSubmit={onVerify} className="space-y-4">
              <CodeInput
                id="code"
                label={useRecoveryCode ? "Recovery code" : "Authentication code"}
                value={code}
                onChange={setCode}
                numeric={!useRecoveryCode}
                disabled={submitting}
                autoFocus
              />

              <Button
                type="submit"
                variant="inverse"
                size="lg"
                loading={submitting}
                disabled={!code.trim()}
                className="w-full"
              >
                Verify sign-in
              </Button>

              <div className="flex flex-col-reverse gap-3 border-t border-line pt-4 text-sm sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="button"
                  onClick={() => {
                    setUseRecoveryCode((current) => !current);
                    setCode("");
                  }}
                  className="text-left text-accent transition-colors duration-200 ease-smooth hover:text-accent-hover"
                >
                  {useRecoveryCode ? "Use authenticator code" : "Use recovery code"}
                </button>
                {timeRemaining !== null && timeRemaining > 0 && (
                  <span className="tabular-nums text-fg-subtle">
                    Expires in {challengeMinutes}:{challengeRemainingSeconds.toString().padStart(2, "0")}
                  </span>
                )}
              </div>
            </form>
          )}
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Sign in" subtitle="Internal console. Staff accounts only.">
      <form onSubmit={onSubmit} className="space-y-5">
        {error && <Alert>{error}</Alert>}

        <Field label="Email" htmlFor="email">
          <Input
            id="email"
            size="xl"
            surface="canvas"
            type="email"
            autoComplete="username"
            required
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>

        {/* The reset link rides the label row rather than sitting under the
            field. Below it, it was one more thing between the last input and
            the submit button — on the path of somebody signing in normally,
            which is almost everybody. */}
        <Field
          label="Password"
          htmlFor="password"
          action={
            <Link
              to="/reset-password"
              state={location.state}
              className="text-sm text-accent transition-colors duration-200 ease-smooth hover:text-accent-hover"
            >
              Forgot password?
            </Link>
          }
        >
          <PasswordInput
            id="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>

        <Button type="submit" variant="inverse" size="lg" loading={submitting} className="w-full">
          Sign in
        </Button>
      </form>
    </AuthShell>
  );
}
